import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Flame, Plus, Timer } from 'lucide-react'
import type { ActivityType } from '@movmania/core'
import { ACTIVITY_TYPES } from '@movmania/core'
import { InsightCard } from '../components/InsightCard.js'
import { QuestList } from '../components/QuestList.js'
import { Bar, Card, Screen, ScreenBody, SectionTitle, StatTile } from '../components/Ui.js'
import { ICON_STROKE } from '../lib/icons.js'
import { photo } from '../lib/photos.js'
import { dayTotals } from '../lib/progress.js'
import { useGame } from '../lib/useGame.js'
import { useT } from '../lib/useT.js'
import { useStore } from '../store/useStore.js'

/** Raccourcis de saisie affichés sur l'accueil, avant « Autre ». */
const QUICK_COUNT = 3

/**
 * L'accueil tient en un regard : où j'en suis cette semaine, un raccourci pour
 * enregistrer ce que je fais le plus souvent, et ce que j'ai fait aujourd'hui.
 * Le détail (graphiques, jauge OMS, journal) vit dans l'onglet Progrès.
 */
export function Home() {
  const t = useT()
  const navigate = useNavigate()
  const game = useGame()
  const profile = useStore((s) => s.profile)
  const activities = useStore((s) => s.activities)

  const name = profile?.displayName ?? ''
  const today = useMemo(() => dayTotals(activities, game.now), [activities, game.now])

  // Les activités préférées d'abord, complétées par les autres si besoin.
  const quick = useMemo(() => {
    const favorites = profile?.favoriteActivities ?? []
    return [...favorites, ...ACTIVITY_TYPES.filter((type) => !favorites.includes(type))].slice(
      0,
      QUICK_COUNT,
    )
  }, [profile])

  const percent = Math.round(game.goalProgress * 100)
  const reached = game.week.totalMinutes >= game.weeklyGoalMin

  return (
    <Screen>
      <ScreenBody className="pb-8">
        {/* Photo plein cadre : elle part du haut de l'écran et se fond dans le
            crème, la carte d'objectif vient mordre dessus. */}
        <div className="relative -mx-5 h-[29rem]">
          <img
            src={photo('hero')}
            alt=""
            className="absolute inset-0 size-full object-cover object-[50%_0%]"
          />
          {/* Voile sombre en haut pour le texte, fondu crème en bas. */}
          <div className="from-night/55 absolute inset-x-0 top-0 h-56 bg-gradient-to-b to-transparent" />
          <div className="from-canvas absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t to-transparent" />

          <div className="safe-top relative px-5">
        <header className="mb-6 flex items-center justify-between">
          <span
            className="bg-green text-surface text-title grid size-12 place-items-center rounded-full font-bold"
            aria-hidden="true"
          >
            {name.charAt(0).toUpperCase()}
          </span>
          {game.streak.current > 0 && (
            <span className="card text-body flex h-12 items-center gap-2 !rounded-full px-4 font-bold">
              <Flame
                size={18}
                strokeWidth={ICON_STROKE}
                className="fill-accent text-accent"
                aria-hidden="true"
              />
              <span className="num">{game.streak.current}</span>
              <span className="text-caption text-ink-3 font-medium">
                {t('week.streak', { count: game.streak.current })}
              </span>
            </span>
          )}
        </header>

        <h1 className="text-display text-surface font-bold [text-shadow:0_2px_16px_rgb(23_19_15/0.45)]">
          {t('home.greeting', { name })}
        </h1>
          </div>
        </div>

        <div className="stagger relative -mt-24 space-y-4">
          <Card>
            <p className="text-title mb-5 max-w-[16rem] font-bold">
              {reached
                ? t('home.goalReached')
                : percent === 0
                  ? t('home.goalStart')
                  : t('home.goalPercent', { percent })}
            </p>
            <div className="mb-2 flex items-baseline justify-between">
              <span className="text-caption text-ink-3">{t('home.thisWeek')}</span>
              <span className="text-caption num">
                <span className="text-ink font-bold">{game.week.totalMinutes}</span>
                <span className="text-ink-3"> / {game.weeklyGoalMin} min</span>
              </span>
            </div>
            <Bar
              progress={game.goalProgress}
              tone={reached ? 'green' : 'accent'}
              height={14}
              label={t('week.goalLabel')}
            />
          </Card>

          <div className="grid grid-cols-4 gap-2">
            {quick.map((type) => (
              <QuickAction
                key={type}
                image={photo(type)}
                label={t(`activityTypeShort.${type}`)}
                ariaLabel={t('home.quickLog', { activity: t(`activityType.${type}`) })}
                onClick={() => navigate('/log', { state: { type } satisfies LogState })}
              />
            ))}
            <QuickAction
              icon={Plus}
              label={t('home.quickOther')}
              ariaLabel={t('home.quickLog', { activity: t('home.quickOther') })}
              onClick={() => navigate('/log')}
            />
          </div>

          <InsightCard insight={game.insight} />
        </div>

        <section className="mt-8">
          <SectionTitle>{t('home.today')}</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <StatTile
              icon={Timer}
              label={t('home.todayMinutes')}
              value={today.minutes}
              unit="min"
            />
            <StatTile
              icon={Flame}
              tone="accent"
              label={t('home.todayKcal')}
              value={today.kcal}
              unit="kcal"
            />
          </div>
        </section>

        <section className="mt-8">
          <SectionTitle>{t('home.quests')}</SectionTitle>
          <div className="space-y-3">
            <QuestList quests={game.dailyQuests} title={t('home.questsDaily')} />
            <QuestList quests={game.weeklyQuests} title={t('home.questsWeekly')} />
          </div>
        </section>
      </ScreenBody>
    </Screen>
  )
}

/** État de navigation accepté par l'écran de saisie. */
export interface LogState {
  type?: ActivityType
}

function QuickAction({
  icon: Icon,
  image,
  label,
  ariaLabel,
  onClick,
}: {
  icon?: typeof Plus
  image?: string
  label: string
  ariaLabel: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      className="press bg-sunken/60 flex min-w-0 flex-col items-center gap-2 rounded-[var(--radius-tile)] px-1 pt-3 pb-3"
    >
      {image ? (
        <img
          src={image}
          alt=""
          className="size-14 rounded-full object-cover shadow-[var(--shadow-soft)]"
        />
      ) : (
        <span className="bg-surface grid size-14 place-items-center rounded-full shadow-[var(--shadow-soft)]">
          {Icon && <Icon size={20} strokeWidth={ICON_STROKE} aria-hidden="true" />}
        </span>
      )}
      <span className="text-caption text-ink-2 w-full truncate text-center font-semibold">
        {label}
      </span>
    </button>
  )
}
